/* Uniform looks: popup handling, variant selection and add to cart.
   Each piece opens on its first in-stock variant. Pieces with no stock at all
   show as sold out and are skipped by "Add the look".
   Works with Dawn style cart drawers (cart-drawer.renderContents) and falls
   back to the cart page when no drawer is present. */

(() => {
  const cartAddUrl = () =>
    `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
  const cartPageUrl = () => (window.routes && window.routes.cart_url) || '/cart';

  if (!customElements.get('uniform-look-product')) {
    class UniformLookProduct extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        const json = this.querySelector('[data-uniform-variants]');
        this.variants = json ? JSON.parse(json.textContent) : [];
        this.soldOut = this.hasAttribute('data-sold-out') || !this.variants.some((v) => v.available);
        this.optionEls = Array.from(this.querySelectorAll('[data-option-index]')).sort(
          (a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex)
        );
        this.addButton = this.querySelector('[data-uniform-add]');
        this.priceEl = this.querySelector('[data-uniform-price]');
        this.imageEl = this.querySelector('.uniform-product__img');
        this.errorEl = this.querySelector('[data-product-error]');
        this.variant = null;

        this.addEventListener('change', (event) => {
          if (!event.target.matches('.uniform-product__radio')) return;
          this.hideError();
          this.update();
        });

        if (this.addButton) {
          this.addButton.addEventListener('click', () => this.onAdd());
        }

        this.ensureAvailableSelection();
        this.update();
      }

      getSelected() {
        return this.optionEls.map((el) => {
          if (el.tagName === 'FIELDSET') {
            const checked = el.querySelector('.uniform-product__radio:checked');
            return checked ? checked.value : null;
          }
          return el.value;
        });
      }

      findVariant(selected) {
        return this.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
      }

      /* Safety net for cached or edited markup: if the checked combination is
         missing or out of stock, check the first in-stock variant instead. */
      ensureAvailableSelection() {
        if (this.soldOut) return;
        const current = this.findVariant(this.getSelected());
        if (current && current.available) return;

        const first = this.variants.find((v) => v.available);
        if (!first) return;

        this.optionEls.forEach((el) => {
          if (el.tagName !== 'FIELDSET') return;
          const value = first.options[Number(el.dataset.optionIndex)];
          el.querySelectorAll('.uniform-product__radio').forEach((input) => {
            input.checked = input.value === value;
          });
        });
      }

      update() {
        const selected = this.getSelected();
        this.variant = selected.includes(null) ? null : this.findVariant(selected);

        this.optionEls.forEach((el) => {
          if (el.tagName !== 'FIELDSET') return;
          const index = Number(el.dataset.optionIndex);
          const value = selected[index];

          if (el.dataset.optionType === 'color') {
            this.renderColorLabels(el, value);
            return;
          }

          // Grey out values with no stock given the other selections (same as PDP sizes).
          el.querySelectorAll('.uniform-product__radio').forEach((input) => {
            const available = this.variants.some(
              (v) =>
                v.available &&
                v.options[index] === input.value &&
                v.options.every((o, i) => i === index || selected[i] === null || o === selected[i])
            );
            const label = el.querySelector(`label[for="${input.id}"]`);
            if (label) label.classList.toggle('is-unavailable', !available);
          });

          const labelEl = el.querySelector('[data-option-label]');
          if (labelEl) labelEl.textContent = value ? `${el.dataset.optionName}: ${value}` : el.dataset.optionName;
        });

        if (this.variant) {
          this.renderPrice(this.variant);
          if (this.variant.image && this.imageEl) {
            this.imageEl.removeAttribute('srcset');
            this.imageEl.src = this.variant.image;
          }
        }

        this.renderButton();
      }

      /* Mirrors the PDP: "Color: Black" heads the core group, "Shades: Pink"
         heads the shades group, and only the active group shows the value. */
      renderColorLabels(fieldset, value) {
        const checked = fieldset.querySelector('.uniform-product__radio:checked');
        const activeGroup = checked ? checked.dataset.group : null;
        fieldset.querySelectorAll('[data-color-group]').forEach((group) => {
          const label = group.querySelector('[data-group-label]');
          if (!label) return;
          const word = label.dataset.groupWord;
          label.textContent = group.dataset.colorGroup === activeGroup && value ? `${word}: ${value}` : word;
        });
      }

      renderPrice(variant) {
        if (!this.priceEl) return;
        this.priceEl.textContent = '';
        if (variant.compare) {
          const s = document.createElement('s');
          s.className = 'uniform-product__compare';
          s.textContent = variant.compare;
          this.priceEl.appendChild(s);
        }
        const span = document.createElement('span');
        span.textContent = variant.price;
        this.priceEl.appendChild(span);
      }

      renderButton() {
        const btn = this.addButton;
        if (!btn) return;

        if (this.soldOut || (this.variant && !this.variant.available)) {
          btn.disabled = true;
          btn.textContent = btn.dataset.labelSoldout || 'Sold out';
        } else if (!this.variant) {
          btn.disabled = true;
          btn.textContent = btn.dataset.labelUnavailable || 'Unavailable';
        } else {
          btn.disabled = false;
          btn.textContent = btn.dataset.labelAdd || 'Add to bag';
        }
      }

      /* status: 'ok' (with item), 'sold-out' (no stock at all, skip),
         or 'unavailable' (in stock, but not in the chosen options). */
      getLineState() {
        if (this.soldOut) return { status: 'sold-out' };
        if (!this.variant || !this.variant.available) return { status: 'unavailable' };
        return { status: 'ok', item: { id: this.variant.id, quantity: 1 } };
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      async onAdd() {
        this.hideError();
        const state = this.getLineState();
        if (state.status !== 'ok') {
          this.showError('This option is sold out. Please choose another.');
          return;
        }

        const section = this.closest('uniform-looks');
        if (!section) return;

        try {
          await section.addToCart([state.item], this.addButton);
        } catch (error) {
          this.showError(error.message);
        }
      }
    }

    customElements.define('uniform-look-product', UniformLookProduct);
  }

  if (!customElements.get('uniform-looks')) {
    class UniformLooks extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.addEventListener('click', (event) => {
          const opener = event.target.closest('[data-uniform-open]');
          if (opener) {
            this.open(document.getElementById(opener.dataset.uniformOpen));
            return;
          }

          if (event.target.closest('[data-uniform-close]')) {
            const dialog = event.target.closest('dialog');
            if (dialog) dialog.close();
            return;
          }

          // Click on the backdrop closes the popup.
          if (event.target.matches('dialog[data-uniform-dialog]')) {
            event.target.close();
            return;
          }

          const addAll = event.target.closest('[data-uniform-add-all]');
          if (addAll) this.onAddAll(addAll);
        });

        this.querySelectorAll('dialog[data-uniform-dialog]').forEach((dialog) => {
          dialog.addEventListener('close', () => {
            if (!this.querySelector('dialog[open]')) {
              document.documentElement.classList.remove('uniform-looks-lock');
            }
          });
        });

        // Theme editor: open the popup for the selected block.
        if (window.Shopify && window.Shopify.designMode) {
          this.onBlockSelect = (event) => {
            if (!this.contains(event.target)) return;
            this.open(event.target.querySelector('dialog[data-uniform-dialog]'));
          };
          this.onBlockDeselect = (event) => {
            if (!this.contains(event.target)) return;
            const dialog = event.target.querySelector('dialog[open]');
            if (dialog) dialog.close();
          };
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }
      }

      disconnectedCallback() {
        document.documentElement.classList.remove('uniform-looks-lock');
        if (this.onBlockSelect) {
          document.removeEventListener('shopify:block:select', this.onBlockSelect);
          document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        }
      }

      open(dialog) {
        if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
        dialog.showModal();
        document.documentElement.classList.add('uniform-looks-lock');
        const close = dialog.querySelector('[data-uniform-close]');
        if (close) close.focus();
      }

      async onAddAll(button) {
        const dialog = button.closest('dialog');
        const errorEl = dialog.querySelector('[data-dialog-error]');
        const setMessage = (text) => {
          if (!errorEl) return;
          errorEl.textContent = text;
          errorEl.hidden = !text;
        };
        setMessage('');

        const products = Array.from(dialog.querySelectorAll('uniform-look-product'));
        const items = [];
        const blocked = [];

        products.forEach((product) => {
          product.hideError();
          const state = product.getLineState();
          if (state.status === 'ok') items.push(state.item);
          if (state.status === 'unavailable') blocked.push(product);
          // 'sold-out' pieces are skipped on purpose.
        });

        if (blocked.length) {
          blocked.forEach((product) =>
            product.showError('This option is sold out. Please choose another.')
          );
          blocked[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          setMessage('Please choose an available option for each piece.');
          return;
        }

        if (!items.length) {
          setMessage('This look is currently sold out.');
          return;
        }

        try {
          await this.addToCart(items, button);
        } catch (error) {
          setMessage(error.message);
        }
      }

      async addToCart(items, button) {
        const cartDrawer = document.querySelector('cart-drawer');
        const canRenderDrawer =
          cartDrawer &&
          typeof cartDrawer.renderContents === 'function' &&
          typeof cartDrawer.getSectionsToRender === 'function';

        const body = { items };
        if (canRenderDrawer) {
          body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        const label = button.textContent;
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;

        try {
          const response = await fetch(cartAddUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Could not add to bag. Please try again.');
          }

          const dialog = button.closest('dialog');
          if (dialog) dialog.close();

          // Dawn declares PUB_SUB_EVENTS with const, so it is global but not on window.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'uniform-looks',
              cartData: data,
            });
          }

          if (canRenderDrawer) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(data);
          } else {
            window.location.href = cartPageUrl();
          }
        } finally {
          button.removeAttribute('aria-busy');
          button.textContent = label;
          const product = button.closest('uniform-look-product');
          if (product) {
            product.renderButton();
          } else {
            button.disabled = false;
          }
        }
      }
    }

    customElements.define('uniform-looks', UniformLooks);
  }
})();